import React, { useState, useEffect, useCallback } from 'react';
import { C } from '../constants/theme';
import { Table } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { Modal } from '../components/Modal';
import { FormField, inputStyle } from '../components/FormField';
import { Button } from '../components/Button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { addOrUpdate, getCategories } from '../utils/CategoryService';
import { formatDate } from '../utils/dateUtils';
import { Badge } from '../components/Badge';
import { Pencil } from 'lucide-react';

const cardStyle = {
  background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
  boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden',
};

const INIT_FORM = { categoryName: '' };

export function ContentCategoryModule() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(INIT_FORM);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState([]);
  const [sortingColumn, setSortingColumn] = useState('id');
  const [direction, setDirection] = useState('ASC');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCategories({
        pageNumber: page,
        pageSize,
        sortingColumn,
        direction,
        filters
      });
      const obj = res.responseObject;
      setData(obj.content);
      setTotal(obj.totalElements);
      setTotalPages(obj.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortingColumn, direction, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setForm(INIT_FORM); setModal('add'); };
  const openEdit = (row) => { setForm({ id: row.id, categoryName: row.categoryName, status: row.status }); setModal(row); };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log(form);
      await addOrUpdate(form);
      setModal(null);
      fetchData();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'categoryName', label: 'Category Name' },
    { key: 'updatedAt', label: 'Updated At', render: (val) => formatDate(val) },
    { key: 'createdAt', label: 'Created At', render: (val) => formatDate(val) },
    { key: 'createdBy', label: 'Created By' },
    { key: 'updatedBy', label: 'Updated By' },
    { key: 'status', label: 'Status', render: (v) => <Badge status={v ? 1 : 0} /> },
  ];

  return (
    <div>
      <PageHeader title="Content Category" count={total} onAdd={openAdd} />
      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Search category…" />
        </div>
        {loading ? <LoadingSpinner /> : (
          <Table columns={columns} data={data} actions={(row) => [
            <Button key="e" small variant="outline" onClick={() => openEdit(row)}>
              <Pencil size={13} strokeWidth={2} />
            </Button>
          ]} />
        )}
        <Pagination
          page={page} totalPages={totalPages} totalElements={total} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        />
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add Content Category' : 'Edit Content Category'} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gap: 18 }}>
            <FormField label="Category Name" required>
              <input
                style={inputStyle}
                value={form.categoryName}
                onChange={(e) => setForm((f) => ({ ...f, categoryName: e.target.value }))}
                placeholder="e.g. ARTICLE"
                autoFocus
              />
            </FormField>
            <FormField label="Status" required>
              <select
                style={inputStyle}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">-- Select Status --</option>
                <option value='1'>Active</option>
                <option value='0'>Inactive</option>
              </select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.categoryName || saving}>
              {saving ? 'Saving…' : modal === 'add' ? 'Create' : 'Update'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
